import useScrollAnimation from '../hooks/useScrollAnimation'
import TrekHeroBanner from '../components/trek/TrekHeroBanner'
import TrekSectionNav from '../components/trek/TrekSectionNav'
import TrekBookingRail from '../components/trek/TrekBookingRail'
import TrekOverview from '../components/trek/TrekOverview'
import TrekHighlights from '../components/trek/TrekHighlights'
import TrekGuideBlock from '../components/trek/TrekGuideBlock'
import TrekReassurance from '../components/trek/TrekReassurance'
import TrekAltitudeProfile from '../components/trek/TrekAltitudeProfile'
import TrekDayByDay from '../components/trek/TrekDayByDay'
import TrekIncluded from '../components/trek/TrekIncluded'
import TrekPermits from '../components/trek/TrekPermits'
import TrekPhotos from '../components/trek/TrekPhotos'
import TrekDepartures from '../components/trek/TrekDepartures'
import TrekFAQGroups from '../components/trek/TrekFAQGroups'
import TrekSimilarTreks from '../components/trek/TrekSimilarTreks'
import TrekWhyUs from '../components/trek/TrekWhyUs'
import TrekReviewsPanel from '../components/trek/TrekReviewsPanel'
import WACTASection from '../components/WACTASection'
import { treks } from '../data/treks'
import { reviews } from '../data/reviews'
import { departures } from '../data/departures'

// One generic trek page, driven entirely by data. Every trek (Manaslu, EBC,
// Annapurna Circuit, Annapurna Base Camp…) renders through this — no per-trek
// page files. Sections and nav tabs appear only when the trek has that data.
export default function TrekPage({ slug }) {
  const trek = treks.find(t => t.slug === slug)
  useScrollAnimation()

  if (!trek) return <div className="section">Trek not found</div>

  const hasGallery = (trek.gallery || []).some(p => p.src && !String(p.src).startsWith('TODO'))
  const hasIncludes = (trek.included || trek.includes || []).length > 0
  const hasDepartures = departures.some(d => d.trekSlug === trek.slug)
  const hasReviews = reviews.some(r => r.trek === trek.name)
  const hasFaq = (trek.faq || []).length > 0
  const hasItinerary = (trek.itinerary || []).length > 0
  const hasPermits = (trek.permits || []).length > 0

  // Nav tabs — only for sections that actually exist on this trek.
  const navSections = [
    { label: 'Overview', id: 'overview' },
    hasItinerary && { label: 'Itinerary', id: 'itinerary' },
    hasIncludes && { label: 'Include/Exclude', id: 'includes' },
    hasDepartures && { label: 'Departure Dates', id: 'departures' },
    hasGallery && { label: 'Gallery', id: 'gallery' },
    hasReviews && { label: 'Reviews', id: 'reviews' },
    hasFaq && { label: 'FAQs', id: 'faqs' },
  ].filter(Boolean)

  return (
    <main className="trek-page" style={{ background: 'var(--white)' }}>
      <TrekHeroBanner trek={trek} />
      <TrekSectionNav sections={navSections} />

      <section id="overview" className="section anchor-target" style={{ paddingBottom: '24px' }}>
        <div style={{ maxWidth: '820px' }}>
          <TrekOverview trek={trek} />
        </div>
      </section>

      {hasGallery && (
        <section id="gallery" className="section anchor-target" style={{ paddingTop: 0 }}>
          <TrekPhotos trek={trek} />
        </section>
      )}

      <section className="section trek-body-grid" style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '60px', alignItems: 'flex-start',
      }}>
        <div className="trek-content">
          <TrekHighlights trek={trek} />
          <TrekGuideBlock trek={trek} />
          <TrekReassurance trek={trek} />
          {hasItinerary && (
            <div id="itinerary" className="anchor-target">
              <TrekAltitudeProfile trek={trek} />
              <TrekDayByDay trek={trek} />
            </div>
          )}
          {hasIncludes && (
            <div id="includes" className="anchor-target">
              <TrekIncluded included={trek.included || trek.includes} excluded={trek.excluded || trek.excludes} />
            </div>
          )}
          {hasDepartures && (
            <div id="departures" className="anchor-target">
              <TrekDepartures trekSlug={trek.slug} />
            </div>
          )}
        </div>

        <TrekBookingRail trek={trek} />
      </section>

      {hasPermits && (
        <section id="permits" className="section anchor-target">
          <div style={{ maxWidth: '820px' }}>
            <TrekPermits trek={trek} />
          </div>
        </section>
      )}

      <TrekSimilarTreks trek={trek} />
      <TrekWhyUs trek={trek} />

      {hasReviews && (
        <section id="reviews" className="section anchor-target" style={{ background: 'var(--white)' }}>
          <TrekReviewsPanel trek={trek} />
        </section>
      )}

      {hasFaq && (
        <section id="faqs" className="section anchor-target bg-stone">
          <TrekFAQGroups trek={trek} />
        </section>
      )}

      <WACTASection />

      <style>{`
        /* min-width:0 lets the content column shrink below its children's
           min-content (e.g. the 520px altitude SVG) instead of forcing the
           whole page wider than a phone screen. */
        .trek-content { min-width: 0; }
        @media (max-width: 1024px) {
          .trek-body-grid { grid-template-columns: minmax(0, 1fr) !important; gap: 40px !important; }
          .mobile-hidden { display: none !important; }
          .sticky-bottom-bar { display: flex !important; }
          .trek-page { padding-bottom: 78px; }
        }
        .sticky-bottom-bar {
          display: none;
          position: fixed;
          bottom: 0; left: 0; right: 0;
          z-index: 150;
          background: var(--white);
          border-top: 1px solid var(--stone-mid);
          padding: 12px 5%;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
        }
      `}</style>
    </main>
  )
}
